import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  BUCKET,
  FILE,
  FILE_ID,
  FILE_URL,
  FILE_URL_EXPIRES,
  ID,
  MIMETYPE,
  NAME,
  ORIGINAL_NAME,
  OFFLINE,
  STATUS,
} from 'api/consts';
import ImagesReview from 'components/ImagesReview';
import { useAsyncState } from 'utils/useAsyncState';

import Item from './Item';
import { Container, Label, NoDocuments, ReadModeWrapper, Title } from './styles';

const EMPTY_TEXT = 'Noch keine Dokumente vorhanden';

const MultiImage = ({
  values,
  label,
  readMode,
  editable,
  updateFile,
  deleteFile,
  loadingDelete,
  onSearch,
  selectable,
  selectedIds,
  onToggleSelect,
  emptyState,
}) => {
  const [imagePath, setImagePath] = useAsyncState();
  const files = useMemo(() => values.map(({ [FILE]: file }) => file), [values]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const closeFile = useCallback(() => setImagePath(), [setImagePath]);
  const resolvedEmpty = emptyState || (readMode ? 'message' : 'none');

  if (!values.length && resolvedEmpty === 'message') {
    return (
      <ReadModeWrapper>
        {label && <Title>{label}</Title>}
        <NoDocuments>{EMPTY_TEXT}</NoDocuments>
      </ReadModeWrapper>
    );
  }

  return (
    <Container>
      {label && <Label>{label}</Label>}
      {!values.length && resolvedEmpty === 'box' && <NoDocuments>{EMPTY_TEXT}</NoDocuments>}
      {values.map(({ [ID]: id, [FILE]: file, [STATUS]: status }) => (
        <Item
          key={id || file[FILE_ID] || file[FILE_URL]}
          file={file}
          id={id}
          status={status}
          readMode={readMode}
          editable={editable}
          selectable={selectable}
          selected={selectedSet.has(id)}
          onToggleSelect={onToggleSelect}
          updateFile={updateFile}
          deleteFile={deleteFile}
          onSearch={onSearch}
          loadingDelete={loadingDelete}
          setImagePath={setImagePath}
        />
      ))}
      {imagePath && <ImagesReview currentImage={imagePath} images={files} onClose={closeFile} />}
    </Container>
  );
};

MultiImage.defaultProps = {
  label: null,
  readMode: false,
  editable: false,
  updateFile: null,
  deleteFile: null,
  loadingDelete: false,
  onSearch: null,
  selectable: false,
  selectedIds: [],
  onToggleSelect: null,
  emptyState: null,
};

MultiImage.propTypes = {
  values: PropTypes.arrayOf(
    PropTypes.shape({
      [ID]: PropTypes.string,
      [STATUS]: PropTypes.number,
      [FILE]: PropTypes.shape({
        [FILE_ID]: PropTypes.string.isRequired,
        [FILE_URL]: PropTypes.string,
        [FILE_URL_EXPIRES]: PropTypes.string,
        [BUCKET]: PropTypes.string.isRequired,
        [MIMETYPE]: PropTypes.string.isRequired,
        [NAME]: PropTypes.string.isRequired,
        [ORIGINAL_NAME]: PropTypes.string.isRequired,
        [OFFLINE]: PropTypes.bool,
      }).isRequired,
    }).isRequired
  ).isRequired,
  label: PropTypes.string,
  readMode: PropTypes.bool,
  editable: PropTypes.bool,
  updateFile: PropTypes.func,
  deleteFile: PropTypes.func,
  loadingDelete: PropTypes.bool,
  onSearch: PropTypes.func,
  selectable: PropTypes.bool,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  onToggleSelect: PropTypes.func,
  emptyState: PropTypes.oneOf(['message', 'box', 'none']),
};

export default MultiImage;
